import { cyrb53 } from './hash';

export interface Rng {
  next(): number;
  range(min: number, max: number): number;
  int(minInclusive: number, maxInclusive: number): number;
  chance(probability: number): boolean;
  pick<T>(items: readonly T[]): T;
  weighted<T>(items: readonly T[], weights: readonly number[]): T;
  normal(mean: number, sd: number): number;
  shuffle<T>(items: readonly T[]): readonly T[];
  fork(label: string): Rng;
}

function sfc32(a: number, b: number, c: number, d: number): () => number {
  return function () {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

function createRngFromState(
  a: number,
  b: number,
  c: number,
  d: number,
  baseSeed: number
): Rng {
  const nextFn = sfc32(a, b, c, d);

  const rng: Rng = {
    next: nextFn,

    range(min: number, max: number): number {
      return min + nextFn() * (max - min);
    },

    int(minInclusive: number, maxInclusive: number): number {
      return Math.floor(minInclusive + nextFn() * (maxInclusive - minInclusive + 1));
    },

    chance(probability: number): boolean {
      return nextFn() < probability;
    },

    pick<T>(items: readonly T[]): T {
      return items[Math.floor(nextFn() * items.length)]!;
    },

    weighted<T>(items: readonly T[], weights: readonly number[]): T {
      let total = 0;
      for (const w of weights) total += w;
      let r = nextFn() * total;
      for (let i = 0; i < items.length; i++) {
        r -= weights[i]!;
        if (r <= 0) return items[i]!;
      }
      return items[items.length - 1]!;
    },

    normal(mean: number, sd: number): number {
      const u1 = nextFn();
      const u2 = nextFn();
      const z = Math.sqrt(-2 * Math.log(u1 || 1e-10)) * Math.cos(2 * Math.PI * u2);
      return mean + sd * z;
    },

    shuffle<T>(items: readonly T[]): readonly T[] {
      const result = [...items];
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(nextFn() * (i + 1));
        [result[i], result[j]] = [result[j]!, result[i]!];
      }
      return result;
    },

    fork(label: string): Rng {
      const hash = cyrb53(label, baseSeed);
      return createRng(hash);
    },
  };

  return rng;
}

export function createRng(seed: number): Rng {
  const s = seed >>> 0;
  return createRngFromState(s, s ^ 0x9e3779b9, s ^ 0x85ebca6b, s ^ 0xc2b2ae35, seed);
}
